<script setup lang="ts">
import { defineAsyncComponent } from '@@/bits/vue'
import { uniqueId } from 'es-toolkit/compat'
import { computed, onBeforeUpdate, ref } from 'vue'
import InlineSvg from 'vue-inline-svg'

const OzEmojiIcon = defineAsyncComponent(() => import('@@/library/v4/components/OzEmojiIcon.vue'))

// Colored SVGs keep the fills in the file. fill-current would paint them with the surrounding text color.
const COLORED_ICON_SOURCES: Record<string, string> = {
  'amazon-color': 'oricons/colored/amazon-color.svg',
  'paypal-color': 'oricons/colored/paypal-color.svg',
  'discover-color': 'oricons/colored/discover-color.svg',
  'jcb-color': 'oricons/colored/jcb-color.svg',
  'mastercard-color': 'oricons/colored/mastercard-color.svg',
  'unionpay-color': 'oricons/colored/unionpay-color.svg',
  'amex-color': 'oricons/colored/amex-color.svg',
  'dinersclub-color': 'oricons/colored/dinersclub-color.svg',
  'visa-color': 'oricons/colored/visa-color.svg',
  google: 'oricons/google.svg',
  windows: 'oricons/windows.svg',
  classlink: 'oricons/classlink.svg',
  skoletube: 'oricons/skoletube.svg',
  twitter: 'oricons/twitter.svg',
  google_drive_color: 'oricons/google_drive_color.svg',
  clever_blue: 'oricons/clever_blue.svg',
  clever_white: 'oricons/clever_white.svg',
}
// @see https://material.io/design/usability/bidirectionality.html#mirroring-elements
const ORICONS_MIRRORING = [
  // Arrows
  'back',
  'arrow_right',
  'highlight_text',
  'arrow_left',
  'arrow_right',
  'chevron_left_center',
  'chevron_left_left',
  'chevron_left_double',
  'chevron_right_center',
  'chevron_right_right',
  'chevron_right_double',
  'skip_backward',
  'skip_forward',
  // Text-related
  'attachment_caption',
  'subscript',
  'superscript',
  'bulleted_list',
  'numbered_list',
  'clipboard',
  'clipboard_outline',
  'comment',
  'quote',
  'enlarge',
  'dock',
  'expand',
  'padlets',
  'send_back',
  'send_front',
  'select_all',

  // Section-related
  'section_arrow_left',
  'section_arrow_right',
  // Movement
  'transfer',
  'external_link',
  'external_link_outline',
  'grid_link',
  'new_frame_above',
  'new_frame_below',
  'duplicate_outline',
  'clear_frame',
  'copy_outline',
  'scissors',
  'send_to_front_outline',
  'send_to_back_outline',
  'pin_filled',

  // Passage of time
  'undo',
  'redo',
  // Others
  'note_filled',
  'note_outline',
]
const WBICONS_MIRRORING = [
  // Text-related/Movement
  'highlighter',
  'eraser',
  // Passage of time
  'undo',
  'redo',
  // Others
  'postnote',
]
/**
 * For list of icons:
 *
 *  - Go to https://icomoon.io/app/#/projects
 *  - Load appropriate project (*Immaterial* or *Oricons*)
 *  - Click *Generate Font* at the bottom
 *
 */

type IconSize = 'small' | 'normal' | 'large'

const {
  dir = 'auto',
  size = 'normal',
  ariaHidden = true,
  font = 'oricon',
  svgVersionKey = '2',
  svgUniqueIds = false,
  name,
} = defineProps<{
  /**
   * If dir="rtl" is included, left-right arrows buttons are flipped
   * @default ltr
   */
  dir?: 'ltr' | 'rtl' | 'auto'
  /**
   * Name of the icon to load
   */
  name: string
  /**
   * Determines the size of the icon
   */
  size?: number | IconSize
  /**
   * Determines if icon should be hidden from screen reader (i.e. only decorative). Default is true.
   */
  ariaHidden?: boolean
  /**
   * Determines the icon font to load
   */
  font?: 'immaterial' | 'oricon' | 'wbicons'
  /**
   * Used to force-refresh SVG icons fetched from our CDN.
   * Change this value to bypass CDN caching and fetch the latest SVG.
   */
  svgVersionKey?: string
  svgUniqueIds?: boolean
}>()

defineEmits<{
  (name: 'click', event: MouseEvent): void
}>()

const isColoredIcon = computed((): boolean => name in COLORED_ICON_SOURCES)

const isMirroringEnabled = computed((): boolean => {
  if (!name) return false
  return (
    (font === 'oricon' && ORICONS_MIRRORING.includes(name)) || (font === 'wbicons' && WBICONS_MIRRORING.includes(name))
  )
})

// @see https://material.io/design/usability/bidirectionality.html#mirroring-elements
//
// Use `inline-block` to force the element to be transformable:
// https://stackoverflow.com/questions/14883250/css-transform-doesnt-work-on-inline-elements
const rtlTransformClasses = computed((): string => {
  if (!isMirroringEnabled.value) return ''
  if (dir === 'rtl') return 'inline-block transform -scale-x-100'
  if (dir === 'auto') return 'inline-block rtl:transform rtl:-scale-x-100'
  return ''
})

const iconSize = computed((): number => {
  let sizeNumber = 24
  if (typeof size === 'number') {
    sizeNumber = size
  } else if (size === 'small') {
    sizeNumber = 18
  } else if (size === 'large') {
    sizeNumber = 32
  }
  return sizeNumber
})

const iconSizeStyles = computed((): string => {
  const sizeNumber = iconSize.value
  return `font-size: ${sizeNumber}px; max-width:${sizeNumber}px; width: ${sizeNumber}px; height: ${sizeNumber}px; overflow: visible;`
})

const svgCustomClasses = computed((): string => {
  if (name === 'padlet_outline') {
    // There’s an issue where exporting the correct stroke width for the Padlet logo specifically breaks the svg, so the exported value is thinner than it should be, and we manually override it here.
    return `stroke-current stroke-[0.5px]`
  }
  return ''
})

const isEmoji = computed((): boolean => {
  const emojiRegex = new RegExp(
    '^(\\p{RI}\\p{RI}|\\p{Extended_Pictographic}(\\p{Emoji_Modifier}|\\u{FE0F}\\u{20E3}?|[\\u{E0020}-\\u{E007E}]+\\u{E007F})?(\\u{200D}(\\p{RI}\\p{RI}|\\p{Emoji}(\\p{Emoji_Modifier}|\\u{FE0F}\\u{20E3}?|[\\u{E0020}-\\u{E007E}]+\\u{E007F})?))*)',
    'u',
  )

  return name.match(emojiRegex) !== null
})

const inlineSvgUrl = computed((): string => {
  const baseUrl = 'https://padlet.net/icons/svg'
  const versionSuffix = svgVersionKey ? `?v=${svgVersionKey}` : ''
  const coloredSource = COLORED_ICON_SOURCES[name]
  if (coloredSource) {
    return `${baseUrl}/${coloredSource}${versionSuffix}`
  }
  const iconType = font === 'wbicons' ? 'wbicons' : 'oricons'
  return `${baseUrl}/${iconType}/${name}.svg${versionSuffix}`
})

/**
 * A race condiiton could be generated in `InlineSvg`
 * causing icons not to update accordingly.
 * We use keys to force a replacement.
 */
const inlineSvgKey = ref<string>(uniqueId('icon-'))

onBeforeUpdate(() => {
  inlineSvgKey.value = uniqueId('icon-')
})
</script>

<script lang="ts">
export default {}
</script>

<template>
  <OzEmojiIcon
    v-if="isEmoji"
    :class="[
      isMirroringEnabled && {
        'inline-block -scale-x-100': dir === 'rtl',
        'inline-block rtl:-scale-x-100': dir === 'auto',
      },
    ]"
    :name="name"
    :data-name="name"
    :style="iconSizeStyles"
    :aria-hidden="String(ariaHidden)"
  />
  <span
    v-else
    class="flex items-center justify-center"
    :style="`width: ${iconSize}px; height: ${iconSize}px`"
    :data-name="name"
  >
    <InlineSvg
      :key="inlineSvgKey"
      :class="[isColoredIcon ? '' : 'fill-current', rtlTransformClasses, svgCustomClasses]"
      style="overflow: visible"
      :src="inlineSvgUrl"
      :width="iconSize"
      :height="iconSize"
      :aria-hidden="String(ariaHidden)"
      :unique-ids="svgUniqueIds"
      alt=""
      @click="$emit('click', $event)"
    />
  </span>
</template>
